import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DocumentsStore, TermSheetStore } from '@qtek/libs/store';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import {
  DocMetaResponse,
  DocumentType,
  SchemaProperty,
} from '@qtek/shared/models';
import { filter, map, race, take, timer } from 'rxjs';
import {
  buildConflictReview,
  ConflictRow,
  FieldSelection,
  parseFieldValue,
} from './credit-agreement-extraction.utils';

export const benchmarkPublishDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'benchmark-publish-dialog'],
  width: '640px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  disableClose: true,
  autoFocus: false,
};

// Uses the loan-terms extraction schema (doc.dtp 450) for both benchmark-gating
// and documents-tab term-sheet validation flows.
const LOAN_TERMS_EXTRACTION_DOC_TYPE = '450';
const EXTRACTION_TIMEOUT_MS = 120000;

export type BenchmarkPublishDialogMode =
  | 'benchmark-publish'
  | 'upload-term-sheet';

interface BenchmarkPublishDialogCopy {
  titleKey: string;
  helperKey: string;
  uploadCtaKey: string;
  retryUploadCtaKey: string;
  extractingStatusKey: string;
  extractionErrorTitleKey: string;
  extractionErrorBodyKey: string;
  noConflictsTitleKey: string;
  noConflictsBodyKey: string;
  reviewNoteKey?: string;
  conflictsActionKey: string;
  noConflictsActionKey: string;
  showNoConflictsActionIcon: boolean;
}

const BENCHMARK_PUBLISH_DIALOG_COPY: Record<
  BenchmarkPublishDialogMode,
  BenchmarkPublishDialogCopy
> = {
  'benchmark-publish': {
    titleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_TITLE',
    helperKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_HELPER',
    uploadCtaKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_UPLOAD_CTA',
    retryUploadCtaKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_RETRY_UPLOAD_CTA',
    extractingStatusKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_EXTRACTION_STATUS',
    extractionErrorTitleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_ERROR_TITLE',
    extractionErrorBodyKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_ERROR_BODY',
    noConflictsTitleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_SUCCESS_TITLE',
    noConflictsBodyKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_SUCCESS_BODY',
    conflictsActionKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_CONFLICTS_ACTION',
    noConflictsActionKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_NO_CONFLICTS_ACTION',
    showNoConflictsActionIcon: false,
  },
  'upload-term-sheet': {
    titleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_TITLE',
    helperKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_HELPER',
    uploadCtaKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_UPLOAD_CTA',
    retryUploadCtaKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_RETRY_UPLOAD_CTA',
    extractingStatusKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_EXTRACTION_STATUS',
    extractionErrorTitleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_ERROR_TITLE',
    extractionErrorBodyKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_ERROR_BODY',
    noConflictsTitleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_SUCCESS_TITLE',
    noConflictsBodyKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_SUCCESS_BODY',
    reviewNoteKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_REVIEW_NOTE',
    conflictsActionKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_CONFLICTS_ACTION',
    noConflictsActionKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_NO_CONFLICTS_ACTION',
    showNoConflictsActionIcon: true,
  },
};

export interface BenchmarkPublishDialogData {
  itmId: string;
  dealName: string;
  /** Merged form snapshot used as the "current value" side of the conflict diff. */
  currentValues: Record<string, unknown> | null;
  properties?: Record<string, SchemaProperty> | null;
  mode?: BenchmarkPublishDialogMode;
}

export interface BenchmarkPublishDialogResult {
  /** Path-keyed values to patch into the deal form before save. */
  resolvedValues: Record<string, unknown>;
  /** Uploaded document that should be retained and classified by the caller. */
  uploadedDocId?: string | null;
}

type Step = 'upload' | 'review';
type UploadPhase = 'idle' | 'uploading' | 'extracting';
type ReviewState = 'has-conflicts' | 'no-conflicts' | 'extraction-failed';

@Component({
  selector: 'qt-benchmark-publish-dialog',
  templateUrl: './benchmark-publish-dialog.component.html',
  styleUrls: ['./benchmark-publish-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
})
export class BenchmarkPublishDialogComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput2') fileInput2?: ElementRef<HTMLInputElement>;

  private readonly dialogRef = inject(
    MatDialogRef<BenchmarkPublishDialogComponent, BenchmarkPublishDialogResult>
  );
  private readonly data = inject<BenchmarkPublishDialogData>(MAT_DIALOG_DATA);
  private readonly documentsStore = inject(DocumentsStore);
  private readonly termSheetStore = inject(TermSheetStore);
  private readonly docMeta$ = toObservable(this.termSheetStore.docMeta);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationService = inject(TranslationCoreService);

  readonly mode: BenchmarkPublishDialogMode =
    this.data.mode ?? 'benchmark-publish';
  readonly dialogCopy = BENCHMARK_PUBLISH_DIALOG_COPY[this.mode];
  readonly dealName =
    this.data.dealName ||
    this.translationService.instant('GNR_UPLOAD_REVIEW_UNTITLED_DEAL');

  readonly step = signal<Step>('upload');
  readonly uploadPhase = signal<UploadPhase>('idle');
  readonly reviewState = signal<ReviewState>('has-conflicts');
  readonly selectedFile = signal<File | null>(null);
  readonly conflictRows = signal<ConflictRow[]>([]);
  readonly saving = signal(false);

  dragCounter = 0;

  /** Tracked so cancel/replace can clean up the uploaded doc. */
  private uploadedDocId: string | null = null;
  /** Per-upload token; flipping `cancelled` lets a late upload-success clean up its own orphan. */
  private activeUploadToken: { cancelled: boolean } | null = null;
  private mysid: string | null = null;

  readonly canAdvanceFromUpload = computed(
    () => this.selectedFile() !== null && this.uploadPhase() === 'idle'
  );

  readonly summaryText = computed(() => {
    const rows = this.conflictRows();
    const conflicts = rows.filter(r => !r.isCurrentEmpty).length;
    const empties = rows.filter(r => r.isCurrentEmpty).length;
    const parts: string[] = [];
    if (conflicts) {
      parts.push(
        this.translationService.instant(
          conflicts === 1
            ? 'GNR_UPLOAD_REVIEW_SUMMARY_CONFLICT'
            : 'GNR_UPLOAD_REVIEW_SUMMARY_CONFLICTS',
          [conflicts]
        )
      );
    }
    if (empties) {
      parts.push(
        this.translationService.instant(
          empties === 1
            ? 'GNR_UPLOAD_REVIEW_SUMMARY_EMPTY_FIELD'
            : 'GNR_UPLOAD_REVIEW_SUMMARY_EMPTY_FIELDS',
          [empties]
        )
      );
    }
    return parts.length
      ? this.translationService.instant(
          'GNR_UPLOAD_REVIEW_SUMMARY_FOUND_IN_DOCUMENT',
          [parts.join(' · ')]
        )
      : this.translationService.instant(
          'GNR_UPLOAD_REVIEW_SUMMARY_DIFFERENCES_FOUND'
        );
  });

  // ─── File pick / drag-drop ────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) this.acceptFile(file);
    if (input) input.value = '';
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.acceptFile(file);
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
  }

  private acceptFile(file: File): void {
    if (!this.isAcceptedExt(file)) return;
    // Mid-upload swap: cancel the prior listener so it disposes the late-arriving doc.
    const wasMidUpload = this.uploadPhase() !== 'idle' && !this.uploadedDocId;
    if (wasMidUpload && this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
    }
    this.disposeUploadedDoc();
    this.selectedFile.set(file);
    // Auto-start on retry-from-failed or mid-upload swap; otherwise wait for Next.
    if (this.step() === 'review' || wasMidUpload) {
      this.step.set('upload');
      this.reviewState.set('has-conflicts');
      this.startUpload();
    }
  }

  private isAcceptedExt(file: File): boolean {
    const name = file.name.toLowerCase();
    return /\.pdf$/.test(name);
  }

  removeFile(event?: Event): void {
    event?.stopPropagation();
    if (this.uploadPhase() !== 'idle') return;
    this.disposeUploadedDoc();
    this.selectedFile.set(null);
  }

  // ─── Upload + extraction ──────────────────────────────

  next(): void {
    if (!this.canAdvanceFromUpload()) return;
    this.startUpload();
  }

  private startUpload(): void {
    const file = this.selectedFile();
    if (!file) return;

    this.uploadPhase.set('uploading');

    // Fresh token per upload — prior listeners keep their own.
    const token = { cancelled: false };
    this.activeUploadToken = token;

    const formData = new FormData();
    formData.append('data', file);
    formData.append('_ks', `itm=${this.data.itmId}`);
    formData.append('doc.dtp', LOAN_TERMS_EXTRACTION_DOC_TYPE);
    formData.append('_acc', '200');
    formData.append('_dsc', file.name);

    // Closure-captured so the cleanup branch survives component destruction.
    const refId = this.data.itmId;
    const documentsStore = this.documentsStore;

    // No takeUntilDestroyed: a mid-upload Cancel must still wait for the HTTP to land so we can dispose the orphan.
    documentsStore
      .uploadDocuments({ data: formData, attr: DocumentType.ITEM_DOCUMENT })
      .subscribe({
        next: docs => {
          const docId = docs?.[0]?._id;
          if (token.cancelled) {
            if (docId) {
              documentsStore
                .deleteDocument({
                  docId,
                  attr: DocumentType.ITEM_DOCUMENT,
                  refId,
                })
                .subscribe();
            }
            return;
          }
          if (docId) {
            this.uploadedDocId = docId;
            this.subscribeExtraction(docId);
          } else {
            this.handleExtractionFailure();
          }
        },
        error: () => {
          if (!token.cancelled) {
            this.handleExtractionFailure();
          }
        },
      });
  }

  private subscribeExtraction(docId: string): void {
    this.uploadPhase.set('extracting');
    this.mysid = `bench_publish_${docId}_${Date.now()}`;
    const mysid = this.mysid;

    this.termSheetStore.connect({
      mysid,
      q: [{ k: 'docId', op: 'EQ', v: [docId] }],
    });

    const docMeta$ = this.docMeta$.pipe(
      map(docMeta => docMeta[mysid]),
      filter(Boolean),
      take(1)
    );

    race(docMeta$, timer(EXTRACTION_TIMEOUT_MS))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value && typeof value === 'object') {
          this.handleExtractionResult(value as DocMetaResponse);
        } else {
          this.handleExtractionFailure();
        }
      });
  }

  private handleExtractionResult(response: DocMetaResponse): void {
    this.uploadPhase.set('idle');
    const data = response?.containers?.[0]?.data as
      | Record<string, string | null>
      | undefined;

    if (!data || data['isValid'] !== 'true') {
      this.handleExtractionFailure();
      return;
    }

    const review = buildConflictReview({
      extracted: data,
      currentValues: this.data.currentValues,
      properties: this.data.properties ?? undefined,
    });

    this.step.set('review');
    if (review.rows.length === 0) {
      this.reviewState.set('no-conflicts');
      this.conflictRows.set([]);
    } else {
      this.reviewState.set('has-conflicts');
      this.conflictRows.set(review.rows);
    }
  }

  private handleExtractionFailure(): void {
    this.uploadPhase.set('idle');
    this.step.set('review');
    this.reviewState.set('extraction-failed');
    this.conflictRows.set([]);
    this.unsubscribeExtraction();
  }

  private unsubscribeExtraction(): void {
    if (this.mysid) {
      this.termSheetStore.disconnect(this.mysid);
      this.mysid = null;
    }
  }

  // ─── Review interactions ──────────────────────────────

  setSelection(key: string, selection: FieldSelection): void {
    this.conflictRows.update(rows =>
      rows.map(r => (r.key === key ? { ...r, selection } : r))
    );
  }

  acceptAllFromDocument(): void {
    this.conflictRows.update(rows =>
      rows.map(r => ({ ...r, selection: 'document' as FieldSelection }))
    );
  }

  back(): void {
    if (this.saving()) return;
    // Dispose the prior doc so a re-upload (Next on same file) doesn't orphan it.
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
    this.step.set('upload');
    this.uploadPhase.set('idle');
  }

  // ─── Confirm + cancel ─────────────────────────────────

  confirmSelection(): void {
    if (this.saving()) return;
    this.saving.set(true);

    const resolved: Record<string, unknown> = {};
    for (const row of this.conflictRows()) {
      if (row.selection === 'document') {
        resolved[row.key] = parseFieldValue(row.documentRaw, row.type);
      }
    }

    const uploadedDocId = this.uploadedDocId;

    // Clear docId/token so destroy-time cleanup doesn't delete the confirmed doc.
    this.uploadedDocId = null;
    this.activeUploadToken = null;
    this.unsubscribeExtraction();
    this.dialogRef.close({ resolvedValues: resolved, uploadedDocId });
  }

  cancel(): void {
    if (this.saving()) return;
    if (this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
      this.activeUploadToken = null;
    }
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
    this.dialogRef.close();
  }

  // ─── Cleanup ──────────────────────────────────────────

  /** Deletes a doc that was uploaded but won't be published (cancel / replace / extraction-fail). */
  private disposeUploadedDoc(): void {
    if (!this.uploadedDocId) return;
    this.documentsStore
      .deleteDocument({
        docId: this.uploadedDocId,
        attr: DocumentType.ITEM_DOCUMENT,
        refId: this.data.itmId,
      })
      .subscribe();
    this.uploadedDocId = null;
  }

  ngOnDestroy(): void {
    // Host destruction without confirm — clean up any uploaded or in-flight doc.
    if (this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
      this.activeUploadToken = null;
    }
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
  }
}
